import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import { MAX_HORSES, MAX_HORSES_FARM_OWNER, GENDERS } from '@horsehub/shared'
import { useMe, useHorses, useUsage, useBilling, useCreateHorse, useMyInvites } from '../api/hooks'
import { HorseCard } from '../components/HorseCard'
import { Input, Select, Modal, PageSpinner, EmptyState } from '../components/ui'
import { fmtBytes } from '../lib/format'
import { errorMessage } from '../lib/errors'

export function Dashboard() {
  const { t } = useI18n()
  const { data: me, isLoading } = useMe()
  const { data: horses } = useHorses()
  const { data: usage } = useUsage()
  const { data: billing } = useBilling()
  const { data: invites } = useMyInvites()
  const [adding, setAdding] = useState(false)

  if (isLoading || !me) return <PageSpinner />
  // Yard owners run a business — their own string of horses isn't capped at 3.
  const horseCap = me?.roles?.farmOwner ? MAX_HORSES_FARM_OWNER : MAX_HORSES
  const atLimit = (horses?.length || 0) >= horseCap

  return (
    <main className="mx-auto max-w-6xl px-5 py-8">
      {invites && invites.length > 0 && (
        <Link to="/my-farm" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-brand-700 px-5 py-3 text-sm text-white">
          <span>📨 {t('farm.accept.heading')}</span>
          <span className="font-semibold underline">{t('farm.accept.cta')}</span>
        </Link>
      )}

      {billing?.plan === 'trial' && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-brand-100/70 dark:bg-brand-800/50 px-5 py-3 text-sm">
          <span>⏳ {t('dashboard.trialBanner', { days: billing.daysLeft })}</span>
          <Link to="/billing" className="font-semibold text-brand-700 dark:text-brand-200 hover:underline">{t('dashboard.trialCta')}</Link>
        </div>
      )}

      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800 text-2xl">
            {me.avatarUrl ? <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" /> : '👤'}
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{t('dashboard.welcome', { name: me.displayName })}</h1>
            <p className="text-brand-600 dark:text-brand-300">{[me.city, me.country].filter(Boolean).join(', ')}</p>
          </div>
        </div>
        <button disabled={atLimit} onClick={() => setAdding(true)} className="btn-primary self-start disabled:opacity-50 sm:self-auto">
          {t('dashboard.addHorse')}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat label={t('dashboard.stats.horses')} value={me?.roles?.farmOwner ? String(horses?.length || 0) : `${horses?.length || 0} / ${horseCap}`} />
        <Stat label={t('dashboard.stats.public')} value={String(horses?.filter((h) => h.public).length || 0)} />
        <div className="card p-5">
          <div className="text-sm text-brand-500 dark:text-brand-400">{t('dashboard.stats.storage')}</div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800">
            <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${usage?.percent ?? 0}%` }} />
          </div>
          <div className="mt-1 text-xs text-brand-500 dark:text-brand-400">
            {usage ? `${usage.percent}% · ${fmtBytes(usage.usedBytes)} / 500 MB` : '—'}
          </div>
        </div>
      </div>

      <div className="mb-4 mt-10 flex items-center justify-between">
        <h2 className="text-xl font-bold">{t('dashboard.myHorses')}</h2>
        {atLimit && <span className="chip">{t('dashboard.comingSoon')}</span>}
      </div>

      {horses && horses.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {horses.map((h) => (
            <HorseCard key={h.id} to={`/app/horse/${h.id}`} showStatus horse={h} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon="🐎"
          title={t('dashboard.empty.title')}
          body={t('dashboard.empty.body')}
          action={<button onClick={() => setAdding(true)} className="btn-primary">{t('dashboard.addHorse')}</button>}
        />
      )}

      {adding && <AddHorseModal onClose={() => setAdding(false)} />}
    </main>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="text-sm text-brand-500 dark:text-brand-400">{label}</div>
      <div className="mt-1 text-2xl font-extrabold">{value}</div>
    </div>
  )
}

function AddHorseModal({ onClose }: { onClose: () => void }) {
  const { t, td } = useI18n()
  const create = useCreateHorse()
  const [f, setF] = useState({ name: '', breed: '', gender: 'Mare', country: '', city: '', dob: '', discipline: '' })
  const [err, setErr] = useState('')
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v })

  const submit = async () => {
    // Name the missing fields instead of leaving a mysteriously dead button.
    const missing = [
      !f.name.trim() && t('horse.fields.name'),
      !f.breed.trim() && t('horse.fields.breed'),
      !f.country.trim() && t('horse.fields.country'),
    ].filter(Boolean) as string[]
    if (missing.length) {
      setErr(t('errors.required', { fields: missing.join(', ') }))
      return
    }
    setErr('')
    try {
      await create.mutateAsync(f as never)
      onClose()
    } catch (e) {
      setErr(errorMessage(e))
    }
  }

  return (
    <Modal title={t('dashboard.addHorse')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('horse.fields.name')} value={f.name} onChange={set('name')} required />
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.breed')} value={f.breed} onChange={set('breed')} required />
          <Select
            label={t('horse.fields.gender')}
            value={f.gender}
            onChange={set('gender')}
            options={GENDERS.map((g) => ({ value: g, label: td('gender', g) }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.country')} value={f.country} onChange={set('country')} required />
          <Input label={t('horse.fields.city')} value={f.city} onChange={set('city')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.dob')} type="date" value={f.dob} onChange={set('dob')} />
          <Input label={t('horse.fields.discipline')} value={f.discipline} onChange={set('discipline')} />
        </div>
        {err && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{err}</p>}
        <button disabled={create.isPending} onClick={submit} className="btn-primary w-full disabled:opacity-60">
          {create.isPending ? `${t('common.save')}…` : t('dashboard.addHorse')}
        </button>
      </div>
    </Modal>
  )
}
